import moment from 'moment-timezone'

import {
  RemindersEditorReminderAPIEntity,
  RemindersEditorReminderUnitValue,
} from '@surglogs-app/network'

import {
  REMINDERS_EDITOR_OPTION_CUSTOM,
  RemindersEditorDefaultOptions,
  RemindersEditorDefaultSelectedOptions,
  ReminderUnitDaysMap,
  ReminderUnitsMap,
} from '../constants'
import type {
  RemindersEditorReminderValue,
  ValidRemindersEditorReminderValue,
} from '../types'

export const getReminderOptionText = (
  reminder: RemindersEditorReminderAPIEntity | RemindersEditorReminderValue,
) => {
  if ('default' in reminder && reminder.default === null) {
    return REMINDERS_EDITOR_OPTION_CUSTOM
  }

  return `${reminder.amount}_${reminder.unit}`
}

export const parseReminderOptionText = (
  text: string,
): { amount: number; unit: RemindersEditorReminderUnitValue } => {
  const [amountStr, unit] = text.split('_')
  const amount = parseInt(amountStr, 10)

  return { amount, unit: unit as RemindersEditorReminderUnitValue }
}

const getAmountInDays = (
  reminder: RemindersEditorReminderValue | RemindersEditorReminderAPIEntity,
) => {
  if (!reminder.unit || !reminder.amount) {
    return 0
  }

  return reminder.amount * ReminderUnitDaysMap[reminder.unit]
}

export const getSortedReminders = (
  reminders: (
    | RemindersEditorReminderValue
    | RemindersEditorReminderAPIEntity
  )[],
) => {
  return reminders.sort((a, b) => {
    return getAmountInDays(a) - getAmountInDays(b)
  })
}

export const getOccupiedReminders = (
  selectedReminders: RemindersEditorReminderValue[],
) =>
  selectedReminders.reduce(
    (acc, selectedReminder) => ({
      ...acc,
      [getReminderOptionText(selectedReminder)]: true,
    }),
    {} as { [key: string]: boolean },
  )

export const isDefaultReminder = (
  reminder: RemindersEditorReminderValue,
  clinicReminders?: RemindersEditorReminderAPIEntity[],
) =>
  !!clinicReminders?.some(
    (defaultReminder) =>
      defaultReminder.unit === reminder.unit &&
      defaultReminder.amount === reminder.amount,
  )

export const getDefaultReminders = (
  reminders: RemindersEditorReminderValue[],
  clinicReminders: RemindersEditorReminderAPIEntity[],
) => {
  return reminders.map((reminder) => ({
    ...reminder,
    default: isDefaultReminder(reminder, clinicReminders),
  }))
}

export const getValidRemindersPayload = (
  reminders: RemindersEditorReminderValue[],
): ValidRemindersEditorReminderValue[] =>
  reminders.map(
    (reminder) =>
      ({
        unit: reminder.unit,
        amount: reminder.amount,
        default: !!reminder.default,
      } as ValidRemindersEditorReminderValue),
  )

export const isExpirationDateAfterOneUnit = (
  exp: string,
  timezone: string,
  unit: RemindersEditorReminderUnitValue,
) =>
  moment(exp).isAfter(
    moment()
      .tz(timezone)
      .tz(moment.tz.guess(true), true)
      .add(1, ReminderUnitsMap[unit]),
  )

export const getFirstValidReminderForExpirationDate = (
  clinicReminders: RemindersEditorReminderAPIEntity[],
  expirationDate: string,
  timezone: string,
) => {
  if (
    !clinicReminders ||
    clinicReminders.length === 0 ||
    !expirationDate ||
    !timezone
  ) {
    return undefined
  }

  const momentExpirationDate = moment(expirationDate).tz(timezone)
  const nowInClinicTimezone = moment().tz(timezone)

  const sortedReminders = getSortedReminders(clinicReminders)

  for (const reminder of sortedReminders) {
    if (!reminder.unit || !reminder.amount) {
      continue
    }

    const reminderDuration = moment.duration(
      reminder.amount,
      ReminderUnitsMap[reminder.unit],
    )
    const reminderDate = nowInClinicTimezone.clone().add(reminderDuration)

    if (momentExpirationDate.isAfter(reminderDate)) {
      return reminder
    }
  }

  return undefined
}

export const getValidRemindersForExpirationDate = (
  clinicReminders: RemindersEditorReminderAPIEntity[],
  expirationDate: string,
  timezone: string,
) => {
  if (clinicReminders.length > 0) {
    const reminder = getFirstValidReminderForExpirationDate(
      clinicReminders,
      expirationDate,
      timezone,
    )

    if (reminder) {
      return [reminder]
    }
  }

  if (
    isExpirationDateAfterOneUnit(
      expirationDate,
      timezone,
      RemindersEditorReminderUnitValue.MONTHS,
    )
  ) {
    return getDefaultReminders(
      RemindersEditorDefaultSelectedOptions,
      clinicReminders,
    )
  }

  if (
    isExpirationDateAfterOneUnit(
      expirationDate,
      timezone,
      RemindersEditorReminderUnitValue.WEEKS,
    )
  ) {
    return getDefaultReminders(
      [RemindersEditorDefaultOptions.ONE_WEEK],
      clinicReminders,
    )
  }

  return [
    {
      unit: null,
      amount: null,
      default: false,
    },
  ]
}

export const isReminderIncludedInDefaultOptions = (
  reminder: RemindersEditorReminderValue,
) =>
  Object.values(RemindersEditorDefaultOptions).some(
    (defaultOption) =>
      defaultOption.unit === reminder.unit &&
      defaultOption.amount === reminder.amount,
  )
